/**
 * Pipeline Orchestrator — Runs all validation stages and produces final verdict.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.9
 *
 * Pipeline flow:
 *   Question → Schema(1) → Graph(2) → Content(3) → Visual(4) → Support(5) → Score(6) → Gate(7) → Verdict
 *
 * Design principles:
 * - N4: Issue Contract governs gates; scoring never overrides blockers
 * - N5: Issue determinism — same input → same issues
 * - N6: No silent data correction in production
 */

import type {
  ValidationIssue,
  PipelineStage,
  PipelineResult,
  PipelineBatchResult,
  GateResult,
  IssueDomain,
  GateVerdict,
  IssueKind,
} from './issueContract'
import {
  evaluateGate,
  deduplicateIssues,
  DEFAULT_GATE_THRESHOLDS,
  computeIssuePenalty,
} from './issueContract'
import { getGateThresholds, isAssessmentEligibleType } from './policyEngine'
import { runSchemaStage } from './stages/schemaStage'
import { runTypeStage } from './stages/typeStage'
import { runContentStage } from './stages/contentStage'
import { runVisualStage, runVisualConsistencyBatch } from './stages/visualStage'
import { runSupportStage } from './stages/supportStage'
import { runGraphStage } from './stages/graphStage'
import { runSeedStage } from './stages/seedStage'
import { runPedagogyStage } from './stages/pedagogyStage'
import type { LDGraph } from '../ldg/types'
import type { Question } from '../../content'

// ─── Pipeline Configuration ───────────────────────────────────────────────────

export interface PipelineOptions {
  /** Built LDG graph for Stage 2 validation */
  graph?: LDGraph
  /** Skip specific stages (for performance or partial runs) */
  skipStages?: PipelineStage[]
  /** Override gate thresholds */
  thresholds?: typeof DEFAULT_GATE_THRESHOLDS
  /** Skip pedagogy stage independently of content stage */
  skipPedagogy?: boolean
}

// ─── Single Question Pipeline ─────────────────────────────────────────────────

/**
 * Run the full governance pipeline on a single question.
 *
 * This is the PRIMARY validation entry point for the v4.0 architecture.
 * It replaces the old `validateQuestion` in `unifiedValidator.ts`.
 */
export function runPipeline(
  question: Question,
  options?: PipelineOptions,
): PipelineResult {
  const skipStages = new Set(options?.skipStages ?? [])
  const allIssues: ValidationIssue[] = []
  const stageResults: Record<PipelineStage, ValidationIssue[]> = {
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
    6: [],
    7: [],
  }
  const stageErrors: Partial<Record<PipelineStage, string>> = {}

  // ── Stage 1: Schema Validation ───────────────────────────────────────────

  if (!skipStages.has(1)) {
    try {
      const schemaIssues = runSchemaStage(question)
      stageResults[1] = schemaIssues
      allIssues.push(...schemaIssues)
    } catch (err) {
      stageErrors[1] = err instanceof Error ? err.message : String(err)
    }
  }

  // ── Stage 1b: Question Type Validation ───────────────────────────────────

  if (!skipStages.has(1)) {
    try {
      const typeIssues = runTypeStage(question)
      stageResults[1] = [...(stageResults[1] ?? []), ...typeIssues]
      allIssues.push(...typeIssues)
    } catch (err) {
      stageErrors[1] =
        (stageErrors[1] ? stageErrors[1] + '; ' : '') +
        (err instanceof Error ? err.message : String(err))
    }
  }

  // ── Stage 2: Graph Validation ────────────────────────────────────────────

  if (!skipStages.has(2)) {
    try {
      const graphIssues = runGraphStage(question, options?.graph)
      stageResults[2] = graphIssues
      allIssues.push(...graphIssues)
    } catch (err) {
      stageErrors[2] = err instanceof Error ? err.message : String(err)
    }
  }

  // ── Stage 3: Content Validation ──────────────────────────────────────────

  if (!skipStages.has(3)) {
    try {
      const contentIssues = runContentStage(question)
      stageResults[3] = contentIssues
      allIssues.push(...contentIssues)
    } catch (err) {
      stageErrors[3] = err instanceof Error ? err.message : String(err)
    }
  }

  // ── Stage 3b: Pedagogy Validation ────────────────────────────────────────

  if (!skipStages.has(3) && !options?.skipPedagogy) {
    try {
      const pedagogyIssues = runPedagogyStage(question)
      stageResults[3] = [...(stageResults[3] ?? []), ...pedagogyIssues]
      allIssues.push(...pedagogyIssues)
    } catch (err) {
      stageErrors[3] =
        (stageErrors[3] ? stageErrors[3] + '; ' : '') +
        (err instanceof Error ? err.message : String(err))
    }
  }

  // ── Stage 4: Visual Validation ───────────────────────────────────────────

  if (!skipStages.has(4)) {
    try {
      const visualIssues = runVisualStage(question)
      stageResults[4] = visualIssues
      allIssues.push(...visualIssues)
    } catch (err) {
      stageErrors[4] = err instanceof Error ? err.message : String(err)
    }
  }

  // ── Stage 5: Support Validation ──────────────────────────────────────────

  if (!skipStages.has(5)) {
    try {
      const supportIssues = runSupportStage(question)
      stageResults[5] = supportIssues
      allIssues.push(...supportIssues)
    } catch (err) {
      stageErrors[5] = err instanceof Error ? err.message : String(err)
    }
  }

  // ── Stage 5b: Seed Consolidation Warnings ────────────────────────────────
  // Surfaces tier/skill/subSkill auto-corrections from the seedConsolidator.
  // Always runs (not skippable) — these are data integrity issues.

  try {
    const seedIssues = runSeedStage(question)
    if (seedIssues.length > 0) {
      stageResults[5] = [...(stageResults[5] ?? []), ...seedIssues]
      allIssues.push(...seedIssues)
    }
  } catch (err) {
    stageErrors[5] =
      (stageErrors[5] ? stageErrors[5] + '; ' : '') +
      (err instanceof Error ? err.message : String(err))
  }

  // ── Stage 6: Scoring (computed in gate evaluation) ───────────────────────
  // No separate scoring stage — score is computed as part of gate evaluation.
  // This keeps scoring deterministic and tied to the issue set.

  // ── Stage 7: Gate Logic ──────────────────────────────────────────────────

  const dedupedIssues = deduplicateIssues(allIssues)
  const thresholds = options?.thresholds ?? getGateThresholds()
  const gateResult = evaluateGate(dedupedIssues, thresholds)

  // Override assessment eligibility based on question type
  if (
    question.questionType &&
    !isAssessmentEligibleType(question.questionType)
  ) {
    gateResult.assessmentEligible = false
  }

  // If any stage errored, force REVIEW_NEEDED so failures are never silent
  const failedStages = Object.keys(stageErrors).map(Number)
  if (failedStages.length > 0 && gateResult.verdict === 'PASS') {
    gateResult.verdict = 'REVIEW_NEEDED'
    gateResult.reviewNeeded = true
    gateResult.reason = `Stage(s) ${failedStages.join(', ')} threw runtime error(s) — review needed`
  }

  // ── Compute domain scores ────────────────────────────────────────────────

  const domainScores = computeDomainScores(dedupedIssues)

  return {
    questionId: question.id ?? 'UNKNOWN',
    familyId: question.familyId ?? 'UNKNOWN',
    gate: gateResult,
    stageResults,
    domainScores,
    ...(failedStages.length > 0 ? { stageErrors } : {}),
    timestamp: Date.now(),
  }
}

// ─── Domain Score Computation ───────────────────────────────────────────────

const ALL_DOMAINS: IssueDomain[] = [
  'schema',
  'type',
  'graph',
  'content',
  'pedagogy',
  'visual',
  'support',
  'seed',
  'scoring',
  'gate',
]

/**
 * Compute per-domain quality scores from deduplicated issues.
 * Each domain starts at 100 and loses points based on issue penalties.
 */
function computeDomainScores(
  issues: ValidationIssue[],
): Record<IssueDomain, number> {
  const scores = {} as Record<IssueDomain, number>
  for (const domain of ALL_DOMAINS) {
    const domainIssues = issues.filter((i) => i.domain === domain)
    const penalty = computeIssuePenalty(domainIssues)
    scores[domain] = Math.max(0, 100 - penalty)
  }
  return scores
}

// ─── Batch Pipeline ─────────────────────────────────────────────────────────

/**
 * Run the governance pipeline on a batch of questions.
 *
 * This is the main entry point for the admin governance dashboard.
 * It runs runPipeline on each question and aggregates the results.
 */
export function runPipelineBatch(
  questions: Question[],
  options?: PipelineOptions,
): PipelineBatchResult {
  const results: PipelineResult[] = questions.map((q) =>
    runPipeline(q, options),
  )

  // Run cross-question visual consistency checks
  const visualConsistencyIssues = runVisualConsistencyBatch(questions)

  // Merge visual consistency issues into per-question results
  for (const issue of visualConsistencyIssues) {
    const target = results.find((r) => r.questionId === issue.questionId)
    if (target) {
      target.gate.issues.push(issue)
      target.gate.counts.total += 1
      if (issue.kind === 'blocker') target.gate.counts.blockers += 1
      else if (issue.kind === 'coach') target.gate.counts.coaches += 1
      else target.gate.counts.infos += 1
    }
  }

  // Aggregate verdicts
  const verdicts: Record<GateVerdict, number> = {
    PASS: 0,
    FAIL: 0,
    REVIEW_NEEDED: 0,
  }
  let totalScore = 0
  let productionCount = 0
  let assessmentCount = 0
  let reviewCount = 0

  const issuesByDomain = {} as Record<IssueDomain, number>
  for (const d of ALL_DOMAINS) issuesByDomain[d] = 0

  const issuesByKind: Record<IssueKind, number> = {
    blocker: 0,
    coach: 0,
    info: 0,
  }

  for (const r of results) {
    verdicts[r.gate.verdict] += 1
    totalScore += r.gate.score
    if (r.gate.productionEligible) productionCount += 1
    if (r.gate.assessmentEligible) assessmentCount += 1
    if (r.gate.reviewNeeded) reviewCount += 1

    for (const issue of r.gate.issues) {
      issuesByDomain[issue.domain] = (issuesByDomain[issue.domain] || 0) + 1
      issuesByKind[issue.kind] += 1
    }
  }

  return {
    generatedAt: new Date().toISOString(),
    totalQuestions: questions.length,
    verdicts,
    productionEligible: productionCount,
    assessmentEligible: assessmentCount,
    reviewNeeded: reviewCount,
    avgScore: results.length > 0 ? Math.round(totalScore / results.length) : 0,
    issuesByDomain,
    issuesByKind,
    results,
  }
}
